import React from "react";
import {
  Button,
  Row,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { CREATE_EMPLOYEE_KPI } from "@/GraphQL/Mutation";
import {
  GET_ALL_KPI_BY_DEPARTMENT_ID,
  GET_ALL_KPI_DURATION,
  GET_EMPLOYEE_KPI_BY_EMPLOYEE_ID,
} from "@/GraphQL/Queries";
import { MultiSelectField } from "@/shared/components/form/MultiSelect";
import FormField from "../FormField";
import { SelectField } from "@/shared/components/form/Select";
import { FaPlus, FaTrash } from "react-icons/fa";
import moment from "moment";
import Swal from "sweetalert2";

export default function CreateEmployeeKpiModal({
  isOpen,
  toggle,
  employeeId,
  deptId,
}) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      duration: null,
      kpis: [{ dept_kpi_id: null, target: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "kpis",
  });

  const { data: kpiDurationData } = useQuery(GET_ALL_KPI_DURATION);
  const { data: kpiData } = useQuery(GET_ALL_KPI_BY_DEPARTMENT_ID, {
    variables: { dept_id: Number(deptId) },
    skip: !Number(deptId),
  });

  const [createEmployeeKpi, { loading }] = useMutation(CREATE_EMPLOYEE_KPI, {
    refetchQueries: [
      {
        query: GET_EMPLOYEE_KPI_BY_EMPLOYEE_ID,
        variables: { employee_id: employeeId },
      },
    ],
  });

  const durationOptions = kpiDurationData?.getAllKpiDuration?.data?.map(
    (item) => ({
      label: `${moment(item.start_date).format("MMMM, YYYY")} to ${moment(
        item.end_date
      ).format("MMMM, YYYY")}`,
      value: item.id,
    })
  );

  const kpiOptions = kpiData?.getAllKpiByDepartmentId?.data?.map((item) => ({
    label: item.key_area,
    value: item.id,
  }));

  const kpiSelections = watch("kpis");

  const onSubmit = async (formData) => {
    try {
      const result = await createEmployeeKpi({
        variables: {
          employee_id: Number(employeeId),
          duration_id: Number(formData?.duration?.value),
          dept_kpi_id: formData.kpis.map((item) => ({
            id: item?.dept_kpi_id?.value,
            target: Number(item?.target),
          })),
        },
      });

      if (result?.data?.createEmployeeKpi?.status === "false") {
        debugger;
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: `${result?.data?.createEmployeeKpi?.message}`,
        });

        toggle();
        reset();
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Employee KPI created successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
      reset();
      toggle();
    } catch (err) {
      console.error("❌ Failed to create employee KPI:", err);
      toast.error("Failed to create Employee KPI.");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Create Employee KPI</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={12}>
              <div className="form__form-group">
                <span className="form__form-group-label">Duration</span>
                <div className="form__form-group-field">
                  <FormField
                    name="duration"
                    control={control}
                    component={SelectField}
                    errors={errors}
                    options={durationOptions}
                    rules={{ required: "Duration is required" }}
                    placeholder="Select Duration"
                  />
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            {fields.map((item, index) => {
              const selectedKpiIds = kpiSelections
                ?.filter((_, i) => i !== index)
                .map((k) => k?.dept_kpi_id?.value)
                .filter(Boolean);

              const filteredKpiOptions = kpiOptions?.filter(
                (opt) => !selectedKpiIds.includes(opt.value)
              );

              return (
                <React.Fragment key={item.id}>
                  <Col md={8}>
                    <div className="form__form-group">
                      <span className="form__form-group-label">KPI</span>
                      <div className="form__form-group-field">
                        <Controller
                          name={`kpis.${index}.dept_kpi_id`}
                          control={control}
                          rules={{ required: "KPI is required" }}
                          render={({ field }) => (
                            <MultiSelectField
                              {...field}
                              options={filteredKpiOptions}
                              placeholder="Select KPI"
                              isMulti={false}
                              onChange={field.onChange}
                            />
                          )}
                        />
                        {errors.kpis?.[index]?.dept_kpi_id && (
                          <small className="text-danger">
                            {errors.kpis[index].dept_kpi_id.message}
                          </small>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col md={2}>
                    <div className="form__form-group">
                      <label className="form__form-group-label">Target</label>
                      <div className="form__form-group-field">
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Target"
                          {...register(`kpis.${index}.target`, {
                            required: "Target is required",
                          })}
                        />
                        {errors.kpis?.[index]?.target && (
                          <small className="text-danger">
                            {errors.kpis[index].target.message}
                          </small>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col md={2} className="d-flex align-items-center mt-4">
                    {index === 0 ? (
                      <Button
                        color="success"
                        type="button"
                        onClick={() =>
                          append({ dept_kpi_id: null, target: "" })
                        }
                      >
                        <FaPlus />
                      </Button>
                    ) : (
                      <Button
                        color="danger"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
